import { type ExternalToast, toast as sonnerToast } from 'sonner';

/** Matches sonner's default auto-close when the caller omits `duration`. */
const DEFAULT_DURATION_MS = 4000;

type ToastApi = typeof sonnerToast;
type ShowFn = (message: never, data?: ExternalToast) => string | number;

function wrapShow(
  fn: ShowFn,
  tryLock: (data?: ExternalToast) => boolean,
): ShowFn {
  return (message, data) => {
    if (!tryLock(data)) {
      return data?.id ?? 'debounced';
    }
    return fn(message, data);
  };
}

/**
 * Same callable surface as sonner `toast`, with one extra rule: when `data.id`
 * is set, a second call with that id is ignored until the toast duration
 * elapses or `toast.dismiss(id)` runs. Sonner's own `id` *updates* the visible
 * toast and resets its timer; mash-clicking a blocked control would keep it
 * on screen. Skipping is the debounce the call sites need.
 */
export function createToast(base: ToastApi): ToastApi {
  const locked = new Map<
    string | number,
    ReturnType<typeof setTimeout> | 'infinite'
  >();

  const unlock = (id: string | number): void => {
    const timer = locked.get(id);
    if (timer && timer !== 'infinite') {
      clearTimeout(timer);
    }
    locked.delete(id);
  };

  const tryLock = (data?: ExternalToast): boolean => {
    const id = data?.id;
    if (id == null) {
      return true;
    }
    if (locked.has(id)) {
      return false;
    }
    const ms = data?.duration ?? DEFAULT_DURATION_MS;
    if (!Number.isFinite(ms)) {
      locked.set(id, 'infinite');
      return true;
    }
    locked.set(
      id,
      setTimeout(() => {
        locked.delete(id);
      }, ms),
    );
    return true;
  };

  const wrapped = wrapShow(base as ShowFn, tryLock) as ToastApi;
  wrapped.success = wrapShow(
    base.success as ShowFn,
    tryLock,
  ) as ToastApi['success'];
  wrapped.info = wrapShow(base.info as ShowFn, tryLock) as ToastApi['info'];
  wrapped.warning = wrapShow(
    base.warning as ShowFn,
    tryLock,
  ) as ToastApi['warning'];
  wrapped.error = wrapShow(base.error as ShowFn, tryLock) as ToastApi['error'];
  wrapped.message = wrapShow(
    base.message as ShowFn,
    tryLock,
  ) as ToastApi['message'];
  wrapped.loading = wrapShow(
    base.loading as ShowFn,
    tryLock,
  ) as ToastApi['loading'];
  wrapped.custom = wrapShow(
    base.custom as ShowFn,
    tryLock,
  ) as ToastApi['custom'];
  wrapped.promise = base.promise;
  wrapped.dismiss = (id?: string | number) => {
    if (id == null) {
      for (const key of [...locked.keys()]) {
        unlock(key);
      }
    } else {
      unlock(id);
    }
    return base.dismiss(id);
  };
  wrapped.getHistory = base.getHistory;
  wrapped.getToasts = base.getToasts;
  return wrapped;
}

/** Annotated so declaration emit names `typeof sonnerToast`, not sonner's unexported `PromiseIExtendedResult` (TS4023). */
export const toast: typeof sonnerToast = createToast(sonnerToast);
